import { forwardRef } from 'react'
import styles from './Fields.module.css'
import { fieldControlClassName } from './fieldStyles'

// Adapted from BoardUI 0.5.9 input/input.tsx, input/label.tsx,
// input/hint-text.tsx, textarea/textarea.tsx, select/select.tsx and
// switch/switch.tsx (https://www.boardui.com/mcp). Studio owns this source.
//
// Keep the native controls at this boundary: callers rely on DOM change events,
// refs, number validity, autofill, option children and browser form reset. The
// vendor TextField/Select composites replace those APIs with React Aria state.
// The field/trigger recipes and SwitchTrack visual are adapted to Wager tokens.
export const InputControl = forwardRef(function InputControl({ size = 'default', className = '', ...props }, ref) {
  return <input {...props} ref={ref} className={fieldControlClassName('input', size, className)} />
})

export const TextareaControl = forwardRef(function TextareaControl({ size = 'default', className = '', ...props }, ref) {
  return <textarea {...props} ref={ref} className={fieldControlClassName('textarea', size, className)} />
})

export const SelectControl = forwardRef(function SelectControl({ size = 'default', className = '', children, ...props }, ref) {
  return <select {...props} ref={ref} className={fieldControlClassName('select', size, className)}>{children}</select>
})

export function FieldLabel({ required = false, children, ...props }) {
  return <label {...props} className={styles.label}>{children}{required ? <span className={styles.required} aria-hidden="true">*</span> : null}</label>
}

export function HintText({ invalid = false, ...props }) {
  return <p {...props} className={invalid ? styles.error : styles.help} />
}

// BoardUI's track → thumb → embossed chip structure is presentation only.
// Native :checked/:focus-visible state keeps controlled and uncontrolled use,
// keyboard operation and form.reset() in sync without a second state store.
export function SwitchTrack() {
  return <span className={styles.switchTrack} aria-hidden="true"><span className={styles.switchThumb}><span className={styles.switchChip} /></span></span>
}

export const SwitchControl = forwardRef(function SwitchControl({
  checked, onChange, label, description, disabled = false, readOnly = false,
  size = 'default', className = '', role = 'switch', ...props
}, ref) {
  return (
    <label className={[
      styles.switch, description ? '' : styles.switchCentered,
      size === 'compact' ? styles.switchCompact : '',
      disabled || readOnly ? styles.switchDisabled : '', className,
    ].filter(Boolean).join(' ')}>
      <input
        {...props}
        ref={ref}
        className={styles.switchInput}
        type="checkbox"
        role={role}
        checked={checked}
        aria-disabled={readOnly || props['aria-disabled'] || undefined}
        onClick={(event) => {
          props.onClick?.(event)
          // Controlled inputs are restored by React after onChange. Cancelling
          // their click too makes the browser roll that restoration back.
          if (readOnly && checked == null) event.preventDefault()
        }}
        onChange={(event) => { if (!readOnly) onChange?.(event.target.checked, event) }}
        disabled={disabled}
      />
      <SwitchTrack />
      <span className={styles.choiceText}>
        <span className={styles.choiceLabel}>{label}</span>
        {description ? <span className={styles.choiceDescription}>{description}</span> : null}
      </span>
    </label>
  )
})
